import { createApi } from "@reduxjs/toolkit/dist/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {
    AssessmentSubmissionRequest,
    AssessmentSubmissionResponse,
} from "@convin/type/Coaching.model";
import {
    ClipUpdatePayload,
    CoachingConfig,
    CoachingTileStats,
    CoachingModuleStage,
    CoachingProgressPayloadType,
    CoachingSession,
    CoachingSessionListPayload,
    ICoachingModule,
    LeadBoardIndividual,
    AssignedCoachingSession,
    CategoryListViewPaylodType,
    CategoryListViewType,
    CreateAssessmentSessionPayloadType,
    CreateAssessmentSessionReponseType,
    CreateCoachingSessionPayloadType,
} from "@convin/type/coaching";
import { PaginationType } from "@convin/type/Common";
import { Team } from "@convin/type/Team";
import { IGlobalFiltersStateContext } from "@convin/contexts/GlobalFiltersStateContextProvider";
import {
    isDefined,
    updateDownloadQue,
} from "@convin/utils/helper/common.helper";
import { UserType } from "@convin/type/User";
import { Assessment } from "@convin/type/lms";
import { DownloadReport } from "@convin/type/Report";

export const coachingApiSlice = createApi({
    reducerPath: "coachingApiSlice",
    baseQuery: axiosBaseQuery(),
    keepUnusedDataFor: 0,
    tagTypes: ["Session", "Response", "AssignedSessions"],
    endpoints: (builder) => ({
        getCoachingSession: builder.query<
            CoachingSession,
            { id: number | string; payload: { reps_id?: number[] } }
        >({
            query: ({ id, payload }) => ({
                url: `/coachings/coaching_session/${id}/`,
                method: "POST",
                body: payload,
            }),
            providesTags: ["Session"],
        }),
        submitCoachingAssessment: builder.mutation<
            { message: string; started_at?: string },
            AssessmentSubmissionRequest & {
                status: "Completed" | "Pending";
            }
        >({
            query: ({ status, ...payload }) => ({
                url: "/coachings/response/create/",
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(
                { submission_id, status },
                { dispatch, queryFulfilled }
            ) {
                await queryFulfilled;
                dispatch(
                    coachingApiSlice.util.updateQueryData(
                        "getCoachingSession",
                        { id: Number(submission_id), payload: {} },
                        (draft) => ({
                            ...draft,
                            assessment: {
                                ...draft.assessment,
                                status,
                            },
                        })
                    )
                );
            },
        }),
        getCoachingAssessmentDetails: builder.query<
            AssessmentSubmissionResponse,
            Pick<
                AssessmentSubmissionRequest,
                "form_id" | "rep_id" | "submission_id"
            >
        >({
            query: (payload) => ({
                url: "/coachings/form_and_response/",
                method: "POST",
                body: payload,
            }),
        }),
        publishCoachingAssessment: builder.mutation<
            void,
            {
                session_id: number;
                is_published_key: boolean;
            }
        >({
            query: (payload) => ({
                url: "/coachings/coaching_session/update/",
                method: "POST",
                body: payload,
            }),
        }),
        getCoachingProgress: builder.mutation<
            CoachingProgressPayloadType,
            Array<{ epoch: number; percentage: number; trend: number }>
        >({
            query: (payload) => ({
                url: "/coachings/completion_graph/",
                method: "POST",
                body: payload,
            }),
        }),
        getCoachingModule: builder.query<
            ICoachingModule,
            { payload: { reps_id: number[] }; id: number }
        >({
            query: ({ payload, id }) => {
                return {
                    url: `/coachings/coaching/module/${id}/`,
                    method: "POST",
                    body: payload,
                };
            },
        }),
        updateCoachingMediaStatus: builder.mutation<
            {
                completed_modules: number;
                updated_module: {
                    completed_at: null | string;
                    id: number;
                };
            },
            { id: number; payload: ClipUpdatePayload }
        >({
            query: ({ id, payload }) => {
                return {
                    url: `/coachings/clip/${id}/`,
                    method: "PATCH",
                    body: payload,
                };
            },

            // updating the cache values
            async onQueryStarted(
                { id, payload },
                { queryFulfilled, dispatch }
            ) {
                try {
                    const { updated_module, completed_modules } = (
                        await queryFulfilled
                    ).data;
                    dispatch(
                        coachingApiSlice.util.updateQueryData(
                            "getCoachingModule",
                            {
                                payload: { reps_id: payload.reps_id },
                                id: payload.module_id,
                            },
                            (draft) => {
                                Object.assign(draft, {
                                    ...draft,
                                    clips: draft.clips.map((clip) =>
                                        clip.id === id
                                            ? {
                                                  ...clip,
                                                  status: payload.status,
                                              }
                                            : clip
                                    ),
                                });
                            }
                        )
                    );
                    dispatch(
                        coachingApiSlice.util.updateQueryData(
                            "getCoachingSession",
                            {
                                id: payload.session_stats_id,
                                payload: { reps_id: [] },
                            },
                            (draft) => {
                                Object.assign(draft, {
                                    ...draft,
                                    completed_modules,
                                    module_stats: draft.module_stats.map(
                                        (module) =>
                                            module.id === updated_module.id
                                                ? {
                                                      ...module,
                                                      completed_clips:
                                                          module.completed_clips +
                                                          1,
                                                      completed_at:
                                                          updated_module.completed_at,
                                                      //   ...(isDefined(
                                                      //       updated_module.completed_at
                                                      //   ) && {
                                                      //       stage: "Completed",
                                                      //   }),
                                                  }
                                                : module
                                    ),
                                });
                            }
                        )
                    );
                } catch (error) {}
            },
        }),
        getCoachingSessionsList: builder.query<
            PaginationType<CoachingSession>,
            CoachingSessionListPayload & {
                next?: string;
            }
        >({
            query: ({ next, ...payload }) => ({
                url: next || "/coachings/coaching_session/",
                method: "POST",
                body: payload,
            }),
        }),
        getAiCoachingSettings: builder.query<
            {
                message: string;
                settings: CoachingConfig;
            },
            void
        >({
            query: () => ({
                url: "/coachings/settings/",
                method: "GET",
            }),
        }),
        updateAiCoachingSettings: builder.mutation<
            {
                message: string;
                settings: CoachingConfig;
            },
            CoachingConfig
        >({
            query: (payload) => ({
                url: "/coachings/settings/",
                method: "PUT",
                body: payload,
            }),

            onQueryStarted: async (payload, { dispatch, queryFulfilled }) => {
                const patchResult = dispatch(
                    coachingApiSlice.util.updateQueryData(
                        "getAiCoachingSettings",
                        undefined,
                        (draft) => {
                            draft.settings = payload;
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch (error) {
                    patchResult.undo();
                }
            },
        }),
        getAiCoachingSettingOptions: builder.query<
            {
                coaching_frequency_choices: ["daily", "weekly", "custom"];
                coaching_date_range_choices: [
                    "yesterday",
                    "last_2_days",
                    "last_3_days",
                    "last_7_days",
                    "week_to_date"
                ];
                teams: Pick<Team, "id" | "name">[];
                parameters: unknown[];
            },
            void
        >({
            query: () => ({
                url: "/coachings/settings/options/",
                method: "GET",
            }),
        }),
        getCoachingModuleStage: builder.query<
            CoachingModuleStage,
            { id: number | string }
        >({
            query: ({ id }) => ({
                url: `/coachings/coaching/module/stats/${id}/`,
                method: "GET",
            }),
        }),
        updateCoachingStage: builder.mutation<
            void,
            {
                id: number | string;
                stage: CoachingModuleStage;
                reps_id: number[];
                module_id: number;
            }
        >({
            query: ({ id, stage }) => ({
                url: `/coachings/coaching/module/stats/progress/${id}/`,
                method: "PATCH",
                body: { stage },
            }),
            async onQueryStarted(
                { stage, reps_id, module_id },
                { dispatch, queryFulfilled }
            ) {
                await queryFulfilled;
                dispatch(
                    coachingApiSlice.util.updateQueryData(
                        "getCoachingModule",
                        {
                            payload: { reps_id },
                            id: Number(module_id),
                        },
                        (draft) => {
                            Object.assign(draft, {
                                ...draft,
                                stage,
                            });
                        }
                    )
                );
            },
        }),
        getCoachingTileStats: builder.query<
            CoachingTileStats,
            {
                payload: IGlobalFiltersStateContext["coachingPayload"];
            }
        >({
            query: ({ payload }) => {
                return {
                    url: "/coachings/manager/tile/",
                    method: "POST",
                    body: payload,
                };
            },
        }),
        getCoachingLeaderboard: builder.query<
            Array<LeadBoardIndividual>,
            {
                payload: IGlobalFiltersStateContext["coachingPayload"];
            }
        >({
            query: ({ payload }) => {
                return {
                    url: "/coachings/manager/leaderboard/",
                    method: "POST",
                    body: payload,
                };
            },
        }),
        getAssignedCoachingSessions: builder.query<
            Array<AssignedCoachingSession>,
            IGlobalFiltersStateContext["coachingPayload"]
        >({
            query: (payload) => {
                return {
                    url: "coachings/assigned_coaching/",
                    method: "POST",
                    body: payload,
                };
            },
            providesTags: ["AssignedSessions"],
        }),
        downloadResponseSheet: builder.mutation<DownloadReport, number>({
            query: (id) => ({
                url: `/coachings/download/${id}/`,
                responseType: "blob",
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                        message:
                            "Response sheet is being downloaded. You will be notified once it is ready.",
                    });
                } catch (err) {}
            },
        }),

        deleteAssignedCoachingSession: builder.mutation<void, number>({
            query: (id) => ({
                url: `coachings/destroy/${id}/`,
                method: "DELETE",
            }),
        }),
        getCategoryListView: builder.query<
            CategoryListViewType,
            CategoryListViewPaylodType & { next?: null | string }
        >({
            query: ({ next = null, ...payload }) => ({
                url: next || "/library/category_list_view/",
                method: "POST",
                body: payload,
            }),
            serializeQueryArgs: ({ queryArgs }) => {
                const newQueryArgs = { ...queryArgs };
                if (newQueryArgs.next) {
                    delete newQueryArgs.next;
                }
                return newQueryArgs;
            },
            merge: (currentCache, newItems) => {
                if (currentCache.results) {
                    return {
                        ...currentCache,
                        ...newItems,
                        results: [...currentCache.results, ...newItems.results],
                    };
                }
                return newItems;
            },
        }),
        createManualCoaching: builder.mutation<
            CreateCoachingSessionPayloadType,
            Partial<CreateCoachingSessionPayloadType>
        >({
            query: (payload) => ({
                url: "/coachings/create_manual_coaching/",
                method: "POST",
                body: payload,
            }),

            invalidatesTags: (_, error) =>
                !isDefined(error) ? ["AssignedSessions"] : [],
        }),
        createManualAssessment: builder.mutation<
            CreateAssessmentSessionReponseType,
            Partial<CreateAssessmentSessionPayloadType>
        >({
            query: (payload) => ({
                url: "/coachings/assessments/create/",
                method: "POST",
                body: payload,
            }),

            invalidatesTags: (_, error) =>
                !isDefined(error) ? ["AssignedSessions"] : [],
        }),
        getAssignedCoachingSessionStats: builder.query<
            AssignedCoachingSession & {
                assessment: Assessment;
                teams: Array<{
                    name: string;
                    id: number;
                    rep_count: number;
                    reps_with_completed_sessions: Array<UserType>;
                    reps_with_in_progress_sessions: Array<UserType>;
                    reps_with_not_started_sessions: Array<UserType>;
                }>;
            },
            {
                payload: IGlobalFiltersStateContext["coachingPayload"];
                id: number;
            }
        >({
            query: ({ payload, id }) => {
                return {
                    url: `coachings/assigned_coaching/stats/${id}/`,
                    method: "POST",
                    body: payload,
                };
            },
        }),
    }),
});

export const {
    useGetCoachingSessionQuery,
    useGetCoachingAssessmentDetailsQuery,
    useSubmitCoachingAssessmentMutation,
    usePublishCoachingAssessmentMutation,
    useGetCoachingProgressMutation,
    useGetCoachingModuleQuery,
    useUpdateCoachingMediaStatusMutation,
    useGetCoachingSessionsListQuery,
    useLazyGetCoachingSessionsListQuery,
    useGetAiCoachingSettingsQuery,
    useGetAiCoachingSettingOptionsQuery,
    useUpdateAiCoachingSettingsMutation,
    useUpdateCoachingStageMutation,
    useGetCoachingModuleStageQuery,
    useGetCoachingTileStatsQuery,
    useGetCoachingLeaderboardQuery,
    useGetAssignedCoachingSessionsQuery,
    useDownloadResponseSheetMutation,
    useDeleteAssignedCoachingSessionMutation,
    useGetCategoryListViewQuery,
    useLazyGetCategoryListViewQuery,
    useCreateManualCoachingMutation,
    useCreateManualAssessmentMutation,
    useGetAssignedCoachingSessionStatsQuery,
} = coachingApiSlice;
